import {
  CCTPDomain,
  bytesToHex,
  fetchAttestation as fetchAttestationUtil,
  getEthTransactionReceipt,
  getMessageTransmitterContract,
  getNetworkConfigs,
  getSolanaConnection,
  getSolanaTokenAccount,
  getTokenMessengerContract,
  getUSDCContract,
  isNonceUsedOnEVM,
  isNonceUsedOnSolana,
  parseLog,
  waitForAttestation as waitForAttestationUtil,
  waitForMessage,
} from "../utils";
import { Interface, defaultAbiCoder, keccak256 } from "ethers/lib/utils";
import tokenMessengerAbi from "./abis/TokenMessenger.json";
import {
  ApproveUSDCOptions,
  BurnUSDCOptions,
  FetchAttestationOptions,
  GetMessageBytesOptions,
  IsTransferCompletedOptions,
  MintUSDCOptions,
} from "../interface";
import { PublicKey } from "@solana/web3.js";

export async function approveUSDC(options: ApproveUSDCOptions) {
  const { signer, amount, sourceDomain, configsSet } = options;
  const networkConfigs = getNetworkConfigs(configsSet, sourceDomain);
  const contract = getUSDCContract(networkConfigs.usdcContractAddress, signer);
  const signerAddress = await signer.getAddress();
  const allowance = await contract.allowance(
    signerAddress,
    networkConfigs.cctpMessengerContractAddress
  );

  if (allowance.lt(amount)) {
    return contract.approve(
      networkConfigs.cctpMessengerContractAddress,
      amount
    );
  }
}

export async function burnUSDC(options: BurnUSDCOptions) {
  const {
    signer,
    sourceDomain,
    destinationDomain,
    destinationAddress,
    amount,
    configsSet,
  } = options;
  const networkConfigs = getNetworkConfigs(configsSet, sourceDomain);

  const contract = getTokenMessengerContract(
    networkConfigs.cctpMessengerContractAddress,
    signer
  );
  let destinationAddressHex = "";

  if (destinationDomain === CCTPDomain.Solana) {
    const destinationNetworkConfigs = getNetworkConfigs(
      configsSet,
      destinationDomain
    );
    const connection = getSolanaConnection(destinationNetworkConfigs.rpc);
    const publicKey = new PublicKey(destinationAddress);
    const userTokenAccount = await getSolanaTokenAccount({
      connection,
      publicKey,
      tokenMintAddress: destinationNetworkConfigs.usdcContractAddress,
    });
    const mintRecipientBuffer = userTokenAccount.toBuffer();
    const mintRecipientBytes = new Uint8Array(32);

    mintRecipientBytes.set(
      mintRecipientBuffer,
      32 - mintRecipientBuffer.length
    );

    destinationAddressHex = bytesToHex(Buffer.from(mintRecipientBytes));
  } else {
    destinationAddressHex = defaultAbiCoder.encode(
      ["address"],
      [destinationAddress]
    );
  }

  return contract.depositForBurn(
    amount,
    destinationDomain,
    destinationAddressHex,
    networkConfigs.usdcContractAddress
  );
}

export async function getMessageBytes(options: GetMessageBytesOptions) {
  const { sourceDomain, burnTxHash, configsSet } = options;
  const { message } = await waitForMessage({
    apiHost: configsSet.irisApiHost,
    domain: sourceDomain,
    transactionHash: burnTxHash,
  });

  const messageHash = keccak256(message);

  return {
    messageBytes: message,
    messageHash,
  };
}

export async function fetchAttestation(options: FetchAttestationOptions) {
  const { configsSet, messageHash } = options;

  if (!configsSet.irisApiHost) {
    throw new Error(`can't find irisApiHost`);
  }

  return fetchAttestationUtil({
    apiHost: configsSet.irisApiHost,
    messageHash,
  });
}

export async function waitForAttestation(options: FetchAttestationOptions) {
  const { configsSet, messageHash } = options;

  if (!configsSet.irisApiHost) {
    throw new Error(`can't find irisApiHost`);
  }

  return waitForAttestationUtil({
    apiHost: configsSet.irisApiHost,
    messageHash,
  });
}

export function mintUSDC(options: MintUSDCOptions) {
  const {
    signer,
    messageBytes,
    attestationSignature,
    destinationDomain,
    configsSet,
  } = options;
  const networkConfigs = getNetworkConfigs(configsSet, destinationDomain);
  const contract = getMessageTransmitterContract(
    networkConfigs.cctpMessageTransmitterContractAddress,
    signer
  );

  return contract.receiveMessage(messageBytes, attestationSignature);
}

export async function isTransferCompleted(options: IsTransferCompletedOptions) {
  const { sourceDomain, burnTxHash, configsSet } = options;
  console.log("isTransferCompleted0");
  const networkConfigs = getNetworkConfigs(configsSet, sourceDomain);
  const burnTx = await getEthTransactionReceipt({
    rpc: networkConfigs.rpc,
    txHash: burnTxHash,
  });
  const iface = new Interface(tokenMessengerAbi);
  const logs = parseLog(iface, burnTx.logs, "DepositForBurn");
  const nonce = logs?.parsedLog.args.nonce;
  const destinationDomain = logs?.parsedLog.args.destinationDomain;
  const destinationNetworkConfigs = getNetworkConfigs(
    configsSet,
    destinationDomain
  );

  const isNonceUsed = await (destinationDomain === CCTPDomain.Solana
    ? isNonceUsedOnSolana({
        networkConfigs: destinationNetworkConfigs,
        sourceDomain,
        nonce: nonce.toNumber(),
      })
    : isNonceUsedOnEVM({
        networkConfigs: destinationNetworkConfigs,
        sourceDomain,
        nonce: nonce.toNumber(),
      }));

  return isNonceUsed;
}
